import type { Simplify } from 'type-fest';
import type { ZodType } from 'zod';

import {
	createEnumNormalizer,
	createJsonNormalizer,
	normalizeBoolean,
	normalizeNumber,
	normalizeString,
} from '@change-corgi/fcm/normalizers';

import type { FcmConfig, FcmMerger, FcmOwner, FcmScope, FcmSupportedGroups, SimplifyWhenEqual } from './types';

type ToConfigOptions = {
	deprecated?: string;
	supportedGroups?: FcmSupportedGroups;
	owner: FcmOwner;
	scope: FcmScope | readonly FcmScope[];
	context?: string;
	description?: string;
	mergeLogic?: string;
	import: 'copy' | 'copy-overwrite' | false;
	template?: unknown;
};

type FactoryFcmConfigWithoutMerger<N extends string, T, D> = Simplify<
	Readonly<{ name: N } & Omit<FcmConfig<T, D>, 'name' | 'merger'>>
>;
type FactoryFcmConfigWithMerger<N extends string, T, D> = Simplify<
	Readonly<{ name: N; merger: FcmMerger<SimplifyWhenEqual<T, D>> } & Omit<FcmConfig<T, D>, 'name' | 'merger'>>
>;

/**
 * A callable builder for a JSON-valued FCM config: calling it with a default value returns the
 * `toConfig`/`withMerger` chain — shaped this way (instead of a plain method) so editors can
 * offer intellisense completion on the call.
 */
type JsonWithDefaultFn<N extends string, T> = {
	(defaultValue: T): {
		withMerger: (merger: FcmMerger<T>) => {
			toConfig: (options: ToConfigOptions) => FactoryFcmConfigWithMerger<N, T, T>;
		};
		toConfig: (options: ToConfigOptions) => FactoryFcmConfigWithoutMerger<N, T, T>;
	};
	(defaultValue: undefined): {
		withMerger: (merger: FcmMerger<T | undefined>) => {
			toConfig: (options: ToConfigOptions) => FactoryFcmConfigWithMerger<N, T, undefined>;
		};
		toConfig: (options: ToConfigOptions) => FactoryFcmConfigWithoutMerger<N, T, undefined>;
	};
};

type FactoryMethods<N extends string> = {
	asBoolean: () => {
		withDefault: (defaultValue: boolean) => {
			withMerger: (merger: FcmMerger<boolean>) => {
				toConfig: (options: ToConfigOptions) => FactoryFcmConfigWithMerger<N, boolean, boolean>;
			};
			toConfig: (options: ToConfigOptions) => FactoryFcmConfigWithoutMerger<N, boolean, boolean>;
		};
	};
	asNumber: () => {
		withDefault: <DEF extends number | undefined>(
			defaultValue: DEF,
		) => {
			toConfig: (
				options: ToConfigOptions,
			) => FactoryFcmConfigWithoutMerger<N, number, DEF extends undefined ? undefined : number>;
		};
	};
	asString: () => {
		withDefault: <DEF extends string | undefined>(
			defaultValue: DEF,
		) => {
			toConfig: (
				options: ToConfigOptions,
			) => FactoryFcmConfigWithoutMerger<N, string, DEF extends undefined ? undefined : string>;
		};
	};
	asEnum: <T extends string>(
		values: readonly T[],
	) => {
		withDefault: <DEF extends T | undefined>(
			defaultValue: DEF,
		) => {
			toConfig: (
				options: ToConfigOptions,
			) => FactoryFcmConfigWithoutMerger<N, T, DEF extends undefined ? undefined : T>;
		};
	};
	asJson: <T>(normalizeParsedValue: ((parsedValue: unknown) => T) | ZodType<T>) => {
		withDefault: JsonWithDefaultFn<N, T>;
	};
};

// eslint-disable-next-line max-lines-per-function
export function defineFcm<N extends string>(name: N): FactoryMethods<N> {
	return {
		asBoolean: () => ({
			withDefault: (defaultValue: boolean) => ({
				withMerger: (merger: FcmMerger<boolean>) => ({
					toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) => ({
						name,
						normalizer: normalizeBoolean,
						defaultValue,
						merger,
						deprecated,
						supportedGroups,
						...details,
					}),
				}),
				toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) => ({
					name,
					normalizer: normalizeBoolean,
					defaultValue,
					deprecated,
					supportedGroups,
					...details,
				}),
			}),
		}),
		asNumber: () => ({
			withDefault: <DEF extends number | undefined>(defaultValue: DEF) => ({
				toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) =>
					({
						name,
						normalizer: normalizeNumber,
						defaultValue,
						deprecated,
						supportedGroups,
						...details,
					}) as FactoryFcmConfigWithoutMerger<N, number, DEF extends undefined ? undefined : number>,
			}),
		}),
		asString: () => ({
			withDefault: <DEF extends string | undefined>(defaultValue: DEF) => ({
				toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) =>
					({
						name,
						normalizer: normalizeString,
						defaultValue,
						deprecated,
						supportedGroups,
						...details,
					}) as FactoryFcmConfigWithoutMerger<N, string, DEF extends undefined ? undefined : string>,
			}),
		}),
		asEnum: <T extends string>(values: readonly T[]) => ({
			withDefault: <DEF extends T | undefined>(defaultValue: DEF) => ({
				toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) =>
					({
						name,
						normalizer: createEnumNormalizer(values),
						defaultValue,
						deprecated,
						supportedGroups,
						...details,
					}) as FactoryFcmConfigWithoutMerger<N, T, DEF extends undefined ? undefined : T>,
			}),
		}),
		asJson: <T>(normalizeParsedValue: ((parsedValue: unknown) => T) | ZodType<T>) => ({
			withDefault: (<DEF extends T | undefined>(defaultValue: DEF) => ({
				withMerger: (merger: FcmMerger<T | DEF extends undefined ? undefined : T>) => ({
					toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) =>
						({
							name,
							normalizer: createJsonNormalizer(normalizeParsedValue),
							defaultValue,
							merger,
							deprecated,
							supportedGroups,
							...details,
						}) as FactoryFcmConfigWithMerger<N, T, DEF extends undefined ? undefined : T>,
				}),
				toConfig: ({ deprecated, supportedGroups, ...details }: ToConfigOptions) =>
					({
						name,
						normalizer: createJsonNormalizer(normalizeParsedValue),
						defaultValue,
						deprecated,
						supportedGroups,
						...details,
					}) as FactoryFcmConfigWithoutMerger<N, T, DEF extends undefined ? undefined : T>,
			})) as JsonWithDefaultFn<N, T>,
		}),
	};
}
